// React
import { memo, useCallback, useState } from "react";

// Helpers
import { classNames, useTranslate } from "helpers";

// Types
import { TableRowChildProps } from "ui/components/Table/TableSearch/types";

// Third-party libs

// UI Components
import { Dialog } from "ui/components/Dialog";
import { deleteFleetDocumentUploadAPI } from "services/api/fleet/vehicles/documents/api";
import { PictureIcon } from "ui/components/Pictures";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";

const VehicleDocumentRowChild: React.FC<TableRowChildProps> = (props) => {
  const { index, row, onFetchSearchResult } = props;

  const { t } = useTranslate();
  const [uploadId, setUploadId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);


  const handleDownloadClick = async (e: React.MouseEvent<HTMLButtonElement>, name: string, url: string) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setIsLoading(true);

      // 1. Fetch file kao blob
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();

      // 2. Napravi blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // 3. Napravi <a> i klikni ga
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();

      // 4. Cleanup
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      setIsLoading(false);
    }
  }

  /** Dokument se otvara u novoj kartici — preglednik sam bira kako će ga prikazati. */
  const handlePreviewClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    event.stopPropagation();
    event.preventDefault();

    window.open(row.url, "_blank", "noopener");
  }

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setUploadId(id);
    setOpenDialog(true);
  }

  const handleDriverDeleteDialogConfirm = useCallback(async () => {
    if (!uploadId) return;

    try {
      const deleted = await deleteFleetDocumentUploadAPI(
        row.documentId,
        uploadId,
        t
      );

      if (deleted && onFetchSearchResult) {
        await onFetchSearchResult();

      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setUploadId("");
    } catch (err) {
      console.error("Greška prilikom brisanja dokumenta:", err);
    }
  }, [uploadId, t]);

  const handleDriverDeleteDialogClose = useCallback(() => {
    setUploadId("");
    setOpenDialog(false);
  }, []);


  return (
    <>
      <tr className={classNames(
        "hover:bg-slate-100",
        index % 2 === 0 ? "bg-white" : " bg-slate-50"
      )}>
        <td className={classNames("px-4 py-2 text-sm font-medium flex-1")}>
          <PictureIcon url={row.url} tooltip={row.originalFileName} />
        </td>
        <td className={classNames("px-4 py-4 text-sm font-medium flex-1")}>
          <button type="button" className="group inline-flex items-center text-sm font-medium underline text-sky-600  text-sm cursor-pointer" onClick={(e) => handleDownloadClick(e, row.originalFileName, row.url)}>  {row.originalFileName}</button>
        </td>

        <td className={classNames("px-4 py-4 text-sm font-medium flex-1")}>
          <ButtonMenu
            endIcon="/media/icons/more-dots.svg"
            cssClassName="h-full"
            variant="text"
          >
            <ButtonMenuItem
              startIcon="/media/icons/preview.svg"
              onClick={handlePreviewClick}
            >
              Pregled dokumenta
            </ButtonMenuItem>
            <ButtonMenuItem
              startIcon="/media/icons/trash.svg"
              variant="remove"
              onClick={(e) => handleDeleteClick(e, row.id)}
            >
              Obriši
            </ButtonMenuItem>
          </ButtonMenu>
        </td>
      </tr>
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.document.upload.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.document.upload.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.document.upload.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.document.upload.dialog.delete.canceltext"
        )}
        onConfirm={handleDriverDeleteDialogConfirm}
        onCancel={handleDriverDeleteDialogClose}
      />
    </>
  );
};

export default memo(VehicleDocumentRowChild);

